import { c as _c } from "react/compiler-runtime";
import type { Color } from '@onefootprint/design-tokens';
import { cx } from 'class-variance-authority';
import type { ReactNode } from 'react';

// One teardrop, so the fill and its hairline edge follow a single shape, over a soft shadow on the ground. Its tip
// is the bottom centre, where Mapbox anchors the marker.
const PIN_PATH = 'M16 1C7.716 1 1 7.716 1 16c0 10.2 13.4 21.6 14.36 22.4a1 1 0 0 0 1.28 0C17.6 37.6 31 26.2 31 16 31 7.716 24.284 1 16 1Z';
type MapMarkerProps = {
  id: string;
  lat: number;
  lng: number;
  label: string;
  getIcon: (color: Color) => ReactNode;
  /** How many entries share this spot, when more than one. */
  count?: number;
  isSelected?: boolean;
  onClick?: () => void;
};

// id, lat and lng are read by the map, which places the marker and passes the clicked id on.
const MapMarker = t0 => {
  const $ = _c(23);
  const {
    label,
    getIcon,
    count,
    isSelected,
    onClick
  } = t0;
  const t1 = isSelected || undefined;
  const t2 = isSelected ? "scale-110" : "hover:scale-105";
  let t3;
  if ($[0] !== t2) {
    t3 = cx("relative flex h-10 w-8 origin-bottom cursor-pointer justify-center transition-transform duration-150 ease-out motion-reduce:transition-none", t2);
    $[0] = t2;
    $[1] = t3;
  } else {
    t3 = $[1];
  }
  let t4;
  if ($[2] === Symbol.for("react.memo_cache_sentinel")) {
    t4 = <span aria-hidden={true} className="absolute -bottom-0.5 left-1/2 h-1.5 w-3 -translate-x-1/2 rounded-full bg-black/20 blur-[2px]" />;
    $[2] = t4;
  } else {
    t4 = $[2];
  }
  const t5 = isSelected ? "fill-(--background-color-accent)" : "fill-(--background-color-primary)";
  let t6;
  if ($[3] !== t5) {
    t6 = cx("absolute inset-0 size-full stroke-black/15", t5);
    $[3] = t5;
    $[4] = t6;
  } else {
    t6 = $[4];
  }
  let t7;
  if ($[5] === Symbol.for("react.memo_cache_sentinel")) {
    t7 = <path d={PIN_PATH} strokeWidth={1} />;
    $[5] = t7;
  } else {
    t7 = $[5];
  }
  let t8;
  if ($[6] !== t6) {
    t8 = <svg aria-hidden={true} viewBox="0 0 32 40" className={t6}>{t7}</svg>;
    $[6] = t6;
    $[7] = t8;
  } else {
    t8 = $[7];
  }
  const t9 = isSelected ? "quinary" : "primary";
  let t10;
  if ($[8] !== getIcon || $[9] !== t9) {
    t10 = getIcon(t9);
    $[8] = getIcon;
    $[9] = t9;
    $[10] = t10;
  } else {
    t10 = $[10];
  }
  let t11;
  if ($[11] !== t10) {
    t11 = <span className="relative mt-2 flex">{t10}</span>;
    $[11] = t10;
    $[12] = t11;
  } else {
    t11 = $[12];
  }
  let t12;
  if ($[13] !== count) {
    t12 = count && <span aria-hidden={true} className="absolute -top-2 -right-3 flex h-4 min-w-4 items-center justify-center rounded-full bg-primary px-1 text-caption-4 text-primary tabular-nums shadow-[0_0_0_1px_rgb(0_0_0/0.12)]">{count}</span>;
    $[13] = count;
    $[14] = t12;
  } else {
    t12 = $[14];
  }
  let t13;
  if ($[15] !== label || $[16] !== onClick || $[17] !== t1 || $[18] !== t11 || $[19] !== t12 || $[20] !== t3 || $[21] !== t8) {
    t13 = <button type="button" aria-label={label} aria-current={t1} onClick={onClick} className={t3}>{t4}{t8}{t11}{t12}</button>;
    $[15] = label;
    $[16] = onClick;
    $[17] = t1;
    $[18] = t11;
    $[19] = t12;
    $[20] = t3;
    $[21] = t8;
    $[22] = t13;
  } else {
    t13 = $[22];
  }
  return t13;
};
export type { MapMarkerProps };
export default MapMarker;